import React, { FunctionComponent, useEffect, useState } from 'react';
import { Icon } from '../../../Component/Icon/Icon';
import { Button } from '../../../Component/Button/Button';

import './VariantSelector.scss';

export interface Variant {
    articleNumber: string;
    imageUri: string | null;
    label: string;
    isMono?: boolean;
}

interface VariantSelectorInterface {
    variants: Variant[];
    closeAction: () => void;
    selectAction: (articleNumber: string) => void;
    isSelected: (variant: Variant) => boolean;
}

export const VariantSelector: FunctionComponent<VariantSelectorInterface> = (props) => {
    const [filterText, setFilterText] = useState<string | undefined>();
    const [filteredVariants, setFilteredVariants] = useState<Variant[]>(props.variants);

    const monoColours = filteredVariants.filter((variant) => variant.isMono);

    useEffect(() => {
        if (filterText !== undefined) {
            setFilteredVariants(
                props.variants.filter(
                    (variant) => variant.label.toLocaleLowerCase().indexOf(filterText.toLocaleLowerCase()) !== -1
                )
            );
        }
    }, [filterText]);

    useEffect(() => {
        setFilteredVariants(props.variants);
    }, [props.variants]);

    useEffect(() => {
        const closeOnEscapePress = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                props.closeAction();
            }
        };
        window.addEventListener('keydown', closeOnEscapePress, false);

        return () => {
            window.removeEventListener('keydown', closeOnEscapePress);
        };
    }, []);

    return (
        <>
            <div className="variant-selector">
                <h2 className="variant-selector__main-headline">Variante wählen</h2>
                <button
                    onClick={props.closeAction}
                    className="variant-selector__close"
                    aria-label="Dialog schließen"
                    type="button"
                >
                    &times;
                </button>

                <div className="variant-selector__filter">
                    <input value={filterText} placeholder="Filter" onChange={(e) => setFilterText(e.target.value)} />
                </div>

                <div
                    className={[
                        'variant-selector__sections',
                        monoColours.length > 0 ? 'variant-selector__sections--has-mono' : '',
                    ].join(' ')}
                >
                    <div>
                        <h3 className="variant-selector__headline">Varianten</h3>
                        <ul className="variant-selector__variants">
                            {filteredVariants
                                .filter((variant) => variant.isMono !== true)
                                .map((variant) => (
                                    <li key={variant.articleNumber}>
                                        <Variant
                                            selectAction={props.selectAction}
                                            variant={variant}
                                            selected={props.isSelected(variant)}
                                        ></Variant>
                                    </li>
                                ))}
                        </ul>
                    </div>

                    {monoColours.length > 0 ? (
                        <div>
                            <h3 className="variant-selector__headline">Mono Farben</h3>

                            <ul className="variant-selector__variants">
                                {monoColours.map((variant) => (
                                    <li key={variant.articleNumber}>
                                        <Variant
                                            selectAction={props.selectAction}
                                            variant={variant}
                                            selected={props.isSelected(variant)}
                                        ></Variant>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                </div>
                <footer className="variant-selector__footer">
                    <Button onClick={props.closeAction} className="variant-selector__apply-button">
                        Übernehmen
                    </Button>
                </footer>
            </div>
            <div className="variant-selector__backdrop" onClick={props.closeAction} />
        </>
    );
};

interface VariantInterface {
    variant: Variant;
    selectAction: (articleNumber: string) => void;
    selected: boolean;
}

export const Variant: FunctionComponent<VariantInterface> = (props) => (
    <button
        className={[
            'variant-selector__variant',
            !props.variant.imageUri ? 'variant-selector__variant--no-image' : '',
        ].join(' ')}
        onClick={() => props.selectAction(props.variant.articleNumber)}
        type="button"
    >
        <div className="variant-selector__variant-description">
            {props.variant.imageUri ? <img src={props.variant.imageUri} /> : null}
            <div className="variant-selector__variant-label">{props.variant.label}</div>
            {props.selected ? <Icon icon="check" className="shopping-planner-selection-marker" /> : null}
        </div>
    </button>
);
